/**
 * Story 052 — Contact section (Email + Phone).
 *
 * AC5: Change-email modal — submit fires POST /me/email and shows a
 * "Verification pending" banner with the new email; the canonical email
 * row still shows the OLD address until verified.
 */
import { useState, type ReactElement } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
// SB6-AUTH-CONSISTENCY-01 — every TextField on this page funnels
// through <AccountField> so chrome is uniform with the other sections.
import { AccountField } from "@aeriosiq/ui";
import { SectionShell } from "./SectionShell.js";
import {
  useChangeEmail,
  useChangePhone,
  type MeExpanded,
} from "../queries/index.js";
import { useToast } from "../ToastContext.js";

interface Props {
  me: MeExpanded;
}

export function ContactSection({ me }: Props): ReactElement {
  return (
    <SectionShell id="contact" title="Contact">
      <Stack spacing={3}>
        <EmailBlock me={me} />
        <PhoneBlock me={me} />
      </Stack>
    </SectionShell>
  );
}

function EmailBlock({ me }: { me: MeExpanded }): ReactElement {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<string | null>(
    me.emailChangePending?.newEmail ?? null,
  );
  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "flex-end" }}
      >
        {/* SB2-W-14 (2026-05-20) — `slotProps.input.readOnly` targets
            MUI's OutlinedInput wrapper, NOT the native `<input>`. React
            therefore saw a controlled `value` with no `onChange` and no
            visible `readOnly`/`disabled` attr, and emitted
            "You provided a `value` prop to a form field without an
            `onChange` handler." every render. The fix routes `readOnly`
            to `htmlInput` so it lands on the native `<input>`, which
            (a) suppresses the React warning at the root cause and
            (b) actually makes the field read-only at the DOM level
            (not just visually). Same change in PhoneBlock below. */}
        <AccountField
          label="Email"
          value={me.email}
          slotProps={{ htmlInput: { readOnly: true } }}
        />
        <Button
          variant="outlined"
          onClick={() => {
            setOpen(true);
          }}
        >
          Change email
        </Button>
      </Stack>
      {/* Plain-text mirror so AC2/AC5 getByText finds the canonical email. */}
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Sign-in email: {me.email}
      </Typography>
      {pending ? (
        <Alert severity="warning" sx={{ mt: 2 }} role="status">
          Verification pending — we sent a link to {pending}. Your sign-in email
          won&apos;t change until you click that link.
        </Alert>
      ) : null}
      <ChangeEmailDialog
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        onPending={(email) => {
          setPending(email);
          setOpen(false);
        }}
      />
    </Box>
  );
}

function ChangeEmailDialog({
  open,
  onClose,
  onPending,
}: {
  open: boolean;
  onClose: () => void;
  onPending: (email: string) => void;
}): ReactElement {
  const [newEmail, setNewEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const change = useChangeEmail();
  const { toast } = useToast();

  function reset() {
    setNewEmail("");
    setConfirmEmail("");
    setPassword("");
    setError(null);
  }

  async function submit() {
    setError(null);
    if (newEmail !== confirmEmail) {
      setError("Email addresses don't match.");
      return;
    }
    try {
      const res = await change.mutateAsync({
        newEmail,
        currentPassword: password,
      });
      if (res.verificationPending) {
        toast("Verification email sent");
        onPending(newEmail);
        reset();
      }
    } catch {
      setError("Couldn't request email change.");
    }
  }

  return (
    <Dialog
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      aria-labelledby="change-email-dialog-title"
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle id="change-email-dialog-title">Change email</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {/* Story 052 AC5 — two separate fields, distinguished by EXACT
              labels. The spec uses `getByLabel('New email', { exact: true })`
              and `getByLabel('Confirm new email', { exact: true })`, which
              resolves the strict-mode locator collision the substring
              regex caused in the first Dev pass. */}
          <AccountField
            label="New email"
            type="email"
            value={newEmail}
            onChange={(e) => {
              setNewEmail(e.target.value);
            }}
            autoComplete="off"
          />
          <AccountField
            label="Confirm new email"
            type="email"
            value={confirmEmail}
            onChange={(e) => {
              setConfirmEmail(e.target.value);
            }}
            autoComplete="off"
          />
          <AccountField
            label="Current password"
            type="password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
            }}
            autoComplete="current-password"
          />
          {error ? (
            <Typography role="alert" color="error" variant="body2">
              {error}
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            reset();
            onClose();
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            void submit();
          }}
          disabled={change.isPending}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function PhoneBlock({ me }: { me: MeExpanded }): ReactElement {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<string | null>(null);
  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "flex-end" }}
      >
        {/* SB2-W-14 — see EmailBlock above; `slotProps.input.readOnly`
            doesn't land on the native `<input>`, so React warned every
            render. Route it through `htmlInput` instead. */}
        <AccountField
          label="Phone"
          value={me.phone?.phoneNumber ?? ""}
          slotProps={{ htmlInput: { readOnly: true } }}
        />
        <Button
          variant="outlined"
          onClick={() => {
            setOpen(true);
          }}
        >
          Change phone
        </Button>
      </Stack>
      {/* Render the current number as text so AC2's getByText finds it. */}
      {me.phone?.phoneNumber ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Current: {me.phone.phoneNumber}
        </Typography>
      ) : null}
      {pending ? (
        <Alert severity="warning" sx={{ mt: 2 }} role="status">
          SMS verification pending — we sent a code to {pending}.
        </Alert>
      ) : null}
      <ChangePhoneDialog
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        onPending={(phone) => {
          setPending(phone);
          setOpen(false);
        }}
      />
    </Box>
  );
}

function ChangePhoneDialog({
  open,
  onClose,
  onPending,
}: {
  open: boolean;
  onClose: () => void;
  onPending: (phoneNumber: string) => void;
}): ReactElement {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const change = useChangePhone();
  const { toast } = useToast();

  async function submit() {
    setError(null);
    try {
      const res = await change.mutateAsync({
        // BE expects `newPhone` per UpdateMyPhoneBodyDto. Renamed
        // 2026-05-25 (SB6-AUTH-PWD-01).
        newPhone: phoneNumber,
        currentPassword: password,
      });
      if (res.verificationPending) {
        toast("Verification SMS sent");
        onPending(phoneNumber);
        setPhoneNumber("");
        setPassword("");
      }
    } catch {
      setError("Couldn't request phone change.");
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="change-phone-dialog-title"
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle id="change-phone-dialog-title">Change phone</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <AccountField
            label="New phone number"
            value={phoneNumber}
            onChange={(e) => {
              setPhoneNumber(e.target.value);
            }}
          />
          <AccountField
            label="Current password"
            type="password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
            }}
            autoComplete="current-password"
          />
          {error ? (
            <Typography role="alert" color="error" variant="body2">
              {error}
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => {
            void submit();
          }}
          disabled={change.isPending}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
